import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Save, Loader2, HandHelping } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function WelcomePanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/welcome/${active.id}`).then((r) => setCfg(r.data));
  }, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;
  if (!cfg) return <Loader />;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/features/welcome/${active.id}`, cfg);
      toast.success("Welcome config saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const textChannels = channels.filter((c) => c.type === "text");

  return (
    <div data-testid="welcome-panel" className="space-y-8 max-w-3xl">
      <div>
        <div className="overline mb-2"><HandHelping className="inline w-4 h-4 mr-2 -mt-1" />ESSENTIALS</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Welcome & Goodbye</h1>
        <p className="text-zinc-400 mt-2">Greet new members and farewell those who leave.</p>
      </div>

      <div className="tactical-card p-6 md:p-8 space-y-5">
        <Toggle label="Welcome Enabled" value={cfg.enabled} onChange={(v) => set("enabled", v)} testId="welcome-toggle" />

        <div>
          <label className="overline block mb-2">Welcome channel</label>
          <select className="tactical-input" value={cfg.channel_id || ""} onChange={(e) => set("channel_id", e.target.value || null)} data-testid="welcome-channel-select">
            <option value="">— Auto (system channel) —</option>
            {textChannels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Welcome message</label>
          <textarea className="tactical-input min-h-[80px]" value={cfg.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} data-testid="welcome-message" />
          <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">Variables: {"{user}"} · {"{server}"}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Embed color</label>
            <input className="tactical-input" type="color" value={cfg.embed_color} onChange={(e) => set("embed_color", e.target.value)} data-testid="welcome-color" />
          </div>
          <div>
            <label className="overline block mb-2">Image URL (optional)</label>
            <input className="tactical-input" placeholder="https://..." value={cfg.image_url || ""} onChange={(e) => set("image_url", e.target.value)} data-testid="welcome-image" />
          </div>
        </div>

        <div className="border-t border-brand-line pt-5">
          <Toggle label="Goodbye Enabled" value={cfg.goodbye_enabled} onChange={(v) => set("goodbye_enabled", v)} testId="goodbye-toggle" />
          <div className="mt-4">
            <label className="overline block mb-2">Goodbye channel</label>
            <select className="tactical-input" value={cfg.goodbye_channel_id || ""} onChange={(e) => set("goodbye_channel_id", e.target.value || null)}>
              <option value="">— Same as welcome —</option>
              {textChannels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div className="mt-4">
            <label className="overline block mb-2">Goodbye message</label>
            <textarea className="tactical-input min-h-[60px]" value={cfg.goodbye_message} onChange={(e) => set("goodbye_message", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="welcome-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!value)} className={`w-full p-3 border flex items-center justify-between rounded-lg ${value ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`} data-testid={testId}>
      <span className="font-heading uppercase tracking-wider text-sm">{label}</span>
      <span className={`w-10 h-5 relative rounded-full ${value ? "bg-brand-orange" : "bg-brand-line"}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full transition-all ${value ? "left-5" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function Loader() {
  return <div className="text-zinc-500 font-mono-tabular text-sm">LOADING…</div>;
}
